import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SiteLogo,
} from "../../Icons";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Linkedin,
  Instagram,
  Facebook,
  ChevronRight
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: "Home", path: "/", isHash: false },
      { name: "Notation Platform", path: "/notation", isHash: false },
      { name: "ROI Calculator", path: "/roi-calculator", isHash: false },
      { name: "Blogs", path: "/blogs", isHash: false },
      { name: "About Us", path: "/about", isHash: false },
      { name: "Contact", path: "/contact", isHash: false },
    ],
    resources: [
      { name: "Try Notation", path: "https://notation.fownd.care/", isHash: false, external: true },
      { name: "Schedule Demo", path: "https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled", isHash: false, external: true },
      { name: "FAQs", path: "/faq", isHash: false },
      { name: "Detailed ROI Calculator", path: "/detailed-roi-calculator", isHash: false },
      { name: "Demo", path: "/demo", isHash: false },
    ],
    legal: [
      { name: "Privacy Policy", path: "/privacy-policy", isHash: false },
      { name: "Cookie Policy", path: "/cookie-policy", isHash: false },
      { name: "Terms & Conditions", path: "/terms-conditions", isHash: false },
    ],
    contact: {
      email: "info@fownd.org",
      phone: "+1 208-500-0415",
      phoneHref: "+12085000415",
      address: "San Francisco, CA"
    }
  };

  const socialLinks = [
    { icon: Linkedin, href: "https://www.linkedin.com/company/fownd-inc/posts/?feedView=all", label: "LinkedIn" },
    { icon: Facebook, href: "https://www.facebook.com/profile.php?id=100095255150461", label: "Facebook" },
    { icon: Instagram, href: "https://www.instagram.com/notation.fownd/", label: "Instagram" }
  ];

  return (
    <footer className="relative bg-[#1F3D3B] overflow-hidden">
      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <SiteLogo className="w-12 h-12 text-white" />
                <span className="text-2xl font-bold text-white">Fownd</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed max-w-sm">
                Revolutionizing clinical documentation with AI-powered solutions that enhance efficiency and improve patient care.
              </p>
              <div className="flex items-center gap-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <social.icon className="w-5 h-5 text-white/80 group-hover:text-white transition-colors duration-300" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-6"
            >
              <h3 className="text-white text-lg font-semibold">Navigation</h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-white text-sm flex items-center group transition-colors duration-300"
                    >
                      <ChevronRight className="w-4 h-4 mr-1 transition-transform duration-300 group-hover:translate-x-1" />
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Resources */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="space-y-6"
            >
              <h3 className="text-white text-lg font-semibold">Resources</h3>
              <ul className="space-y-3">
                {footerLinks.resources.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    {link.external ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/70 hover:text-white text-sm flex items-center group transition-colors duration-300"
                      >
                        <ChevronRight className="w-4 h-4 mr-1 transition-transform duration-300 group-hover:translate-x-1" />
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="text-white/70 hover:text-white text-sm flex items-center group transition-colors duration-300"
                      >
                        <ChevronRight className="w-4 h-4 mr-1 transition-transform duration-300 group-hover:translate-x-1" />
                        {link.name}
                      </Link>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Legal Links */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="space-y-6"
            >
              <h3 className="text-white text-lg font-semibold">Legal</h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-white text-sm flex items-center group transition-colors duration-300"
                    >
                      <ChevronRight className="w-4 h-4 mr-1 transition-transform duration-300 group-hover:translate-x-1" />
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Section */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-6"
            >
              <h3 className="text-white text-lg font-semibold">Contact</h3>
              <ul className="space-y-4">
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 }}
                  className="flex items-start gap-3 group"
                >
                  <Mail className="w-5 h-5 text-white/70 group-hover:text-white transition-colors duration-300 mt-0.5" />
                  <a
                    href={`mailto:${footerLinks.contact.email}`}
                    className="text-white/70 hover:text-white text-sm transition-colors duration-300"
                  >
                    {footerLinks.contact.email}
                  </a>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="flex items-start gap-3 group"
                >
                  <Phone className="w-5 h-5 text-white/70 group-hover:text-white transition-colors duration-300 mt-0.5" />
                  <a
                    href={`tel:${footerLinks.contact.phoneHref}`}
                    className="text-white/70 hover:text-white text-sm transition-colors duration-300"
                  >
                    {footerLinks.contact.phone}
                  </a>
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="flex items-start gap-3 group"
                >
                  <MapPin className="w-5 h-5 text-white/70 group-hover:text-white transition-colors duration-300 mt-0.5" />
                  <span className="text-white/70 group-hover:text-white text-sm transition-colors duration-300">
                    {footerLinks.contact.address}
                  </span>
                </motion.li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16 pt-8 border-t border-white/10"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} Fownd. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="https://notation.fownd.care/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-white/80 text-sm font-medium flex items-center gap-2 group"
              >
                Try Notation
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;

